import * as React from 'react';
import { useCallback, useId, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { alpha, useTheme } from '@mui/material/styles';

// ************** Types **************

export type FileUploadProps = {
    message: string;
    accept: string;
    onFileSelect?: (file: File) => void;
};

// ************** Component **************

const HIFileUpload: React.FC<FileUploadProps> = ({
    message,
    accept,
    onFileSelect,
}) => {
    const theme = useTheme();
    const [file, setFile] = useState<File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const inputId = useId();

    const successColor = theme.palette.success.main;
    const primaryColor = theme.palette.primary.main;
    const dividerColor = theme.palette.divider;

    const handleFile = useCallback(
        (f: File) => {
            setFile(f);
            onFileSelect?.(f);
        },
        [onFileSelect],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files[0];
            if (f) handleFile(f);
        },
        [handleFile],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOver(false);
    }, []);

    const handleFileInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
        },
        [handleFile],
    );

    const handleClick = useCallback(() => {
        document.getElementById(inputId)?.click();
    }, [inputId]);

    const borderColor = file
        ? successColor
        : dragOver
            ? primaryColor
            : dividerColor;
    const backgroundColor = file
        ? alpha(successColor, 0.04)
        : dragOver
            ? alpha(primaryColor, 0.08)
            : 'transparent';

    return (
        <Box
            onClick={handleClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
                width: '100%',
                maxWidth: 500,
                minHeight: 200,
                border: `2px ${file ? 'solid' : 'dashed'} ${borderColor}`,
                borderRadius: Number(theme.shape.borderRadius) * 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 3,
                cursor: 'pointer',
                transition: 'border-color 0.2s, background-color 0.2s',
                backgroundColor,
                textAlign: 'center',
                '&:hover': {
                    borderColor: file ? successColor : primaryColor,
                    backgroundColor: file
                        ? alpha(successColor, 0.04)
                        : alpha(primaryColor, 0.04),
                },
            }}
        >
            <input
                id={inputId}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleFileInput}
            />
            {file ? (
                <>
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        Fichier chargé
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {file.name}
                    </Typography>
                </>
            ) : (
                <>
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'action.active' }} />
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        {message}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        ou
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        sx={{ mt: 1 }}
                        component="span"
                    >
                        Télécharger
                    </Button>
                </>
            )}
        </Box>
    );
};

export default HIFileUpload;
